import React, {useState, useEffect, useContext, lazy, Suspense} from 'react';
import {useRouteMatch, Switch, Route, Redirect} from 'react-router-dom';
import {Row, Col, Spin, Tabs, Input} from 'antd';
import {OpenApiContext} from '../openapi-context';
import {ApiIndex} from '../components/interfaces/api-index';
import {WebhookIndex} from '../components/interfaces/webhook-index';
import {ModelView} from '../components/models/model-view';
import {ParameterView} from '../components/models/parameter-view';
import {InterfaceView} from '../components/interfaces/interface-view';
import {FrontPage} from '../components/overview/front-page';
import {Overview} from '../components/overview/overview';
import {ServersView} from '../components/overview/servers-view';
import {AddModelDialog} from '../components/add-model-dialog';
import {AddInterfaceDialog} from '../components/add-interface-dialog';
import {AddWebhookDialog} from '../components/add-webhook-dialog';
import {ContactView} from '../components/overview/contact-view';
import {LicenseView} from '../components/overview/license-view';
import {omit, isEmpty, intersection} from 'lodash';
import {ComponentIndex} from '../components/component-index';
import {SecuritySchemeView} from '../components/models/security-scheme-view';
import {isAIEnabled} from '../ai/feature-flag';
import {AppContext} from '../state';

// AI add-on lazy-imported so the chunk is only fetched for tenants
// with the feature in their license. Free tier never downloads it.
const AIChatPanel = lazy(() =>
    import('../ai').then((m) => ({default: m.AIChatPanel})),
);


const MODEL_TYPES = ['parameters', 'models', 'securitySchemes'];

export function ApiView({mobile, version, isLoading, spec, updateSpec, currentHash}) {
    const openapiContext = useContext(OpenApiContext);
    const {editorProps = {}} = useContext(AppContext);
    const {params} = useRouteMatch('/:env/standards/:category/:key/:version?/:view?');
    const [filter, setFilter] = useState();
    const [schemaCount, setSchemaCount] = useState(0);
    const [parameterCount, setParameterCount] = useState(0);
    const [securitySchemeCount, setSecuritySchemeCount] = useState(0);
    const [intfsCount, setIntfsCount] = useState();
    const [webhooksCount, setWebhooksCount] = useState(0);
    const [activePane, setActivePane] = useState('overview');
    const [mobileWidthView, setMobileWidthView] = useState(false);

    // Webhooks tab shows only for 3.1 specs. For 3.1 specs in read-only with
    // no webhooks defined, we still show the tab (with the "+ Add Webhook"
    // affordance disabled) when the user is an editor — so they can add one.
    // For 3.0 specs, the tab is never rendered.
    const isOpenApi31 = typeof spec?.openapi === 'string' && spec.openapi.startsWith('3.1');
    const hasWebhooks = openapiContext.state && typeof openapiContext.state.webhooks === 'function' ? openapiContext.state.webhooks().length > 0 : false;

    useEffect(() => {
        if (openapiContext.state.schemas) {
            setSchemaCount(openapiContext.state.schemas(filter).length);
            setParameterCount(openapiContext.state.parameters(filter).length);
            setSecuritySchemeCount(openapiContext.state.securitySchemes(filter).length);
            setIntfsCount(openapiContext.state.interfaces(filter).length);
            if (typeof openapiContext.state.webhooks === 'function') {
                setWebhooksCount(openapiContext.state.webhooks(filter).length);
            } else {
                setWebhooksCount(0);
            }
        }
    }, [openapiContext.state, filter, currentHash]);

    useEffect(() => {
        if (params.view) {
            handlePaneChange(params.view);
        } else {
            setActivePane('overview');
        }
    }, [params.view]);

    function handleDeleteModel(model) {
        switch (model.componentType) {
            case 'parameter':
                updateSpec({type: 'delete-parameter-def', name: model.name});
                break;
            case 'securityScheme':
                updateSpec({type: 'delete-security-scheme-def', name: model.name});
                break;
            default:
                updateSpec({type: 'delete-model', name: model.name});
                break;
        }
    }

    function handleAddModel(model) {
        updateSpec({
            type: 'add-model',
            name: model.name,
            modelType: model.type || 'object',
            schemaType: model.schemaType,
            operator: model.operator,
            model: omit(model, 'name', 'type', 'schemaType', 'operator'),
        });
    }

    function handleAddInterface(intf) {
        const collectionMethods = intf.idField ? intersection(['get', 'post'], intf.methods) : intf.methods;
        const collectionEndpoints = intersection(collectionMethods).reduce((spec, method) => {
            spec[method] = {
                responses: intf.responseTypes.reduce((responses, type) => {
                    const schema =
                        method === 'get' && intf.model
                            ? {
                                  type: 'array',
                                  items: {
                                      $ref: `#/components/schemas/${intf.model.name}`,
                                  },
                              }
                            : intf.model
                            ? {$ref: `#/components/schemas/${intf.model.name}`}
                            : {properties: {}};
                    responses['200'] = {
                        content: {},
                        description: 'Success',
                    };
                    responses['200'].content[type] = {schema};
                    responses['500'] = {
                        content: {},
                        description: 'Server Error',
                    };
                    responses['500'].content[type] = {schema: {}};

                    responses['400'] = {
                        content: {},
                        description: 'Client Error',
                    };
                    responses['400'].content[type] = {schema: {}};

                    return responses;
                }, {}),
            };

            if (method === 'post' || method === 'put' || method === 'patch') {
                spec[method].requestBody = {
                    content: {},
                };
                spec[method].requestBody.content['application/json'] = {};
                if (intf.model) {
                    spec[method].requestBody.content['application/json'].schema = {$ref: `#/components/schemas/${intf.model.name}`};
                } else {
                    spec[method].requestBody.content['application/json'].schema = {type: 'object', properties: {}};
                }
            }
            return spec;
        }, {});

        const globalParams = intf.path.match(/{[^}]+}/g);
        if (globalParams) {
            collectionEndpoints.parameters = globalParams.map((globalParam) => {
                return {
                    name: globalParam.substring(1, globalParam.length - 1),
                    in: 'path',
                    description: '',
                    schema: {
                        type: 'string',
                    },
                    required: true,
                };
            });
        }

        updateSpec({type: 'add-interface', path: intf.path, intf: collectionEndpoints});

        if (intf.idField) {
            const itemEndpoints = intersection(['get', 'put', 'patch', 'delete'], intf.methods).reduce((spec, method) => {
                spec[method] = {
                    responses: intf.responseTypes.reduce((responses, type) => {
                        let schema = {type: 'object'};
                        if (method !== 'delete') {
                            if (intf.model) {
                                schema = {$ref: `#/components/schemas/${intf.model.name}`};
                            }
                        } else {
                            schema = {type: 'boolean'};
                        }

                        responses['200'] = {
                            content: {},
                            description: 'Success',
                        };
                        responses['200'].content[type] = {schema};
                        responses['500'] = {
                            content: {},
                            description: 'Server Error',
                        };
                        responses['500'].content[type] = {schema: {}};

                        responses['400'] = {
                            content: {},
                            description: 'Client Error',
                        };
                        responses['400'].content[type] = {schema: {}};

                        return responses;
                    }, {}),
                };

                if (method === 'put' || method === 'patch') {
                    spec[method].requestBody = {
                        content: {},
                    };
                    spec[method].requestBody.content['application/json'] = {};
                    if (intf.model) {
                        spec[method].requestBody.content['application/json'].schema = {$ref: `#/components/schemas/${intf.model.name}`};
                    } else {
                        spec[method].requestBody.content['application/json'].schema = {type: 'object', properties: {}};
                    }
                }

                const globalParams = intf.path.match(/{[^}]+}/g);
                if (globalParams) {
                    spec.parameters = globalParams.map((globalParam) => {
                        return {
                            name: globalParam.substring(1, globalParam.length - 1),
                            in: 'path',
                            description: '',
                            schema: {
                                type: 'string',
                            },
                            required: true,
                        };
                    });
                }

                return spec;
            }, {});

            itemEndpoints.parameters = itemEndpoints.parameters || [];
            itemEndpoints.parameters.push({
                name: intf.idField,
                in: 'path',
                schema: {
                    type: 'string',
                },
                description: intf.model ? `Unique ID of the ${intf.model.name}` : '',
                required: true,
            });

            updateSpec({type: 'add-interface', path: `${intf.path}/{${intf.idField}}`, intf: itemEndpoints});
        }
    }

    function handleDeleteInterface(intf) {
        updateSpec({type: 'delete-interface', path: intf.path});
    }

    function handleAddWebhook(wh) {
        // Create a minimal PathItem skeleton keyed by the webhook name.
        // Webhooks most commonly define a POST (server pushes the event to
        // the subscriber's URL), so we skeleton that one with a 200-OK.
        const skeleton = {
            post: {
                summary: wh.summary || undefined,
                requestBody: {
                    description: `Event payload for ${wh.name}`,
                    content: {'application/json': {schema: {type: 'object', properties: {}}}},
                },
                responses: {
                    200: {description: 'Return 200 to acknowledge receipt'},
                },
            },
        };
        // Strip undefined so they don't serialize.
        if (!skeleton.post.summary) delete skeleton.post.summary;
        updateSpec({type: 'add-webhook', name: wh.name, webhook: skeleton});
    }

    function handleDeleteWebhook(wh) {
        updateSpec({type: 'delete-webhook', name: wh.name});
    }

    const canEdit = () => openapiContext.SUPPORTED_EDITOR_ROLES.indexOf(openapiContext.state.role) !== -1;

    // Tab labels are tight on horizontal space when the right pane is
    // open (sidebar span 5/24 ≈ 280px on a 1366 screen). Each label
    // carries its inline `+` add-button, which together with three
    // visible tabs pushes Webhooks into the antd `…` overflow dropdown.
    // Two changes keep all four tabs visible:
    //   1. The `+` add buttons collapse to a single icon-only control
    //      with no surrounding text, with a small left margin so it
    //      reads as part of the tab.
    //   2. Counts ride next to the label without parens (Models·6
    //      style) — fewer chars, no whitespace before/after the paren.
    const ModelTabTitle = () => (
        <span className="api-tab-title">
            Models{canEdit() && !isEmpty(spec) && (
                <AddModelDialog disabled={openapiContext.state.editMode} onAddModel={handleAddModel} />
            )}
        </span>
    );
    const InterfaceTabTitle = ({count}) => (
        <span className="api-tab-title">
            APIs <span className="api-tab-count">{count}</span>
            {canEdit() && !isEmpty(spec) && (
                <AddInterfaceDialog disabled={openapiContext.state.editMode} onAddInterface={handleAddInterface} />
            )}
        </span>
    );
    const WebhookTabTitle = ({count}) => (
        <span className="api-tab-title">
            Webhooks <span className="api-tab-count">{count}</span>
            {canEdit() && !isEmpty(spec) && (
                <AddWebhookDialog disabled={openapiContext.state.editMode} onAddWebhook={handleAddWebhook} />
            )}
        </span>
    );

    const clearFilter = (filterValue) => {
        if (filterValue === '') {
            setFilter(filterValue);
        }
    };

    function checkMobileView() {
        if (mobile === true) {
            setMobileWidthView(!mobileWidthView);
        }
    }

    useEffect(() => {
        if (mobile === true && mobileWidthView === true && params && (params.view === 'models' || params.view === 'interfaces')) {
            setMobileWidthView(false);
        }
    }, [params.view]);

    function handlePaneChange(key) {
        if (MODEL_TYPES.indexOf(key) !== -1) {
            setActivePane('models');
        } else {
            setActivePane(key);
        }
    }

    // AI add-on: when the tenant has it, the layout is 5+13+6.
    // When they don't, free-tier behavior is preserved by collapsing
    // back to 7+17 — the AI chunk is never even requested.
    //
    // editorProps.aiEnabled, when explicitly set (true OR false) by the
    // portal, overrides the URL/window/env feature-flag. Standalone
    // mode leaves it undefined and falls through to isAIEnabled().
    const aiOn = !mobile && (
        editorProps.aiEnabled !== undefined ? !!editorProps.aiEnabled : isAIEnabled()
    );
    // §10(e): portal-supplied right-pane slots (e.g. Comments). When
    // present they show alongside the AI Chat panel via tabs; when
    // alone they take the right column directly. Empty/undefined ⇒
    // existing AI-only behavior.
    const rightPanelSlots = Array.isArray(editorProps.rightPanelSlots) ? editorProps.rightPanelSlots : [];
    const hasRight = aiOn || rightPanelSlots.length > 0;
    // Right-column width tuning (gap #7). Defaults: AI Chat needs the
    // wider lane (span 6 ≈ 25%) so the streaming-message column doesn't
    // wrap awkwardly; pure portal slots (Comments-style) default to span 5
    // (≈ 21%) which leaves more room for the spec content in the middle.
    // A slot can override via `slot.width` (an integer 3–8 in the antd
    // 24-col grid). When AI and a slot share the column we still take the
    // wider of the two — the user toggles tabs and the column has to host
    // whichever one's visible.
    const slotWidthHints = rightPanelSlots
        .map((s) => Number(s && s.width))
        .filter((n) => Number.isFinite(n) && n >= 3 && n <= 8);
    const slotMaxWidth = slotWidthHints.length ? Math.max(...slotWidthHints) : null;
    let rightSpan;
    if (!hasRight) {
        rightSpan = 0;
    } else if (aiOn) {
        rightSpan = slotMaxWidth ? Math.max(6, slotMaxWidth) : 6;
    } else {
        rightSpan = slotMaxWidth || 5;
    }
    // Sidebar at span=5 was too tight for the four tabs (Overview /
    // Models / APIs / Webhooks) — Webhooks fell into the antd `…`
    // overflow dropdown. 6 keeps them all on one row at the cost of one
    // grid column from the middle pane.
    const sidebarSpan = mobile ? 24 : hasRight ? 6 : 7;
    const mainSpan = mobile ? 24 : hasRight ? 24 - sidebarSpan - rightSpan : 17;

    // Build the tab list for the right column. AI Chat is the first
    // tab when active; portal slots follow in declaration order. With
    // a single tab we skip the antd Tabs chrome and render the panel
    // directly — same look the editor had before §10(e).
    const rightTabItems = [];
    if (aiOn) {
        rightTabItems.push({
            key: 'ai',
            label: 'AI Chat',
            children: (
                <Suspense fallback={null}>
                    <AIChatPanel mobile={mobile} />
                </Suspense>
            ),
        });
    }
    for (const slot of rightPanelSlots) {
        if (!slot || !slot.key) continue;
        rightTabItems.push({
            key: slot.key,
            label: slot.label || slot.key,
            children: slot.component,
        });
    }

    return (
        <Row>
            {(mobile === false || mobileWidthView === false) && (
                <Col span={sidebarSpan}>
                    <div className="sidebar">
                        <Input.Search
                            disabled={openapiContext.state.editMode}
                            allowClear
                            style={{border: 0}}
                            placeholder="filter using keywords"
                            onChange={(event) => clearFilter(event.target.value)}
                            onSearch={setFilter}
                        />
                        <Tabs
                            disabled={openapiContext.state.editMode}
                            animated={false}
                            activeKey={activePane}
                            onChange={handlePaneChange}
                            items={[
                                {
                                    key: 'overview',
                                    label: <span style={{position: 'relative', top: -3}}>Overview</span>,
                                    children: <Overview version={version} checkMobileView={checkMobileView} mobile={mobile} />,
                                },
                                {
                                    key: 'models',
                                    label: <ModelTabTitle />,
                                    children: (
                                        <ComponentIndex
                                            currentHash={currentHash}
                                            params={params}
                                            filter={filter}
                                            schemaCount={schemaCount}
                                            parameterCount={parameterCount}
                                            securitySchemeCount={securitySchemeCount}
                                            onDelete={handleDeleteModel}
                                            checkMobileView={checkMobileView}
                                        />
                                    ),
                                },
                                {
                                    key: 'interfaces',
                                    label: <InterfaceTabTitle count={intfsCount} />,
                                    children: <ApiIndex currentHash={currentHash} filter={filter} onDelete={handleDeleteInterface} params={params} checkMobileView={checkMobileView} mobile={mobile} />,
                                },
                                // Webhooks tab: 3.1 only. Hidden entirely for
                                // 3.0 specs so their UI is unchanged.
                                ...(isOpenApi31
                                    ? [
                                          {
                                              key: 'webhooks',
                                              label: <WebhookTabTitle count={webhooksCount} />,
                                              children: (
                                                  <WebhookIndex
                                                      currentHash={currentHash}
                                                      filter={filter}
                                                      onDelete={handleDeleteWebhook}
                                                      params={params}
                                                      checkMobileView={checkMobileView}
                                                      mobile={mobile}
                                                  />
                                              ),
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    </div>
                </Col>
            )}
            {mobileWidthView === true && (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <a className="nestedTextView" onClick={checkMobileView} alt="goback">
                    click here to Go Back
                </a>
            )}
            <Col span={mainSpan} id="mobile-view-container">
                {isLoading && <Spin size="large" />}
                {(mobile === false || mobileWidthView === true) && (
                    <Switch>
                        <Route path={`/${params.env}/standards/${params.category}/${params.key}/${version}/interfaces/:intfKey`}>
                            <InterfaceView mobile={mobile} spec={spec} dispatch={updateSpec} rootPath={`${params.category}/${params.key}`} source="paths" />
                        </Route>
                        <Route path={`/${params.env}/standards/${params.category}/${params.key}/${version}/webhooks/:intfKey`}>
                            <InterfaceView mobile={mobile} spec={spec} dispatch={updateSpec} rootPath={`${params.category}/${params.key}`} source="webhooks" />
                        </Route>
                        <Route path={`/${params.env}/standards/${params.category}/${params.key}/${version}/models/:modelKey`}>
                            <ModelView mobile={mobile} spec={spec} dispatch={updateSpec} rootPath={`${params.category}/${params.key}`} currentHash={currentHash} />
                        </Route>
                        <Route path={`/${params.env}/standards/${params.category}/${params.key}/${version}/parameters/:modelKey`}>
                            <ParameterView mobile={mobile} spec={spec} dispatch={updateSpec} rootPath={`${params.category}/${params.key}`} />
                        </Route>
                        <Route path={`/${params.env}/standards/${params.category}/${params.key}/${version}/securitySchemes/:modelKey`}>
                            <SecuritySchemeView mobile={mobile} spec={spec} dispatch={updateSpec} rootPath={`${params.category}/${params.key}`} />
                        </Route>
                        <Route exact path={`/${params.env}/standards/${params.category}/${params.key}/${version}/overview/contact`}>
                            <ContactView spec={spec} params={params} dispatch={updateSpec} version={version} rootPath={`${params.category}/${params.key}`} />
                        </Route>
                        <Route exact path={`/${params.env}/standards/${params.category}/${params.key}/${version}/overview/license`}>
                            <LicenseView spec={spec} params={params} dispatch={updateSpec} version={version} rootPath={`${params.category}/${params.key}`} />
                        </Route>
                        <Route exact path={`/${params.env}/standards/${params.category}/${params.key}/${version}/overview/servers`}>
                            <ServersView spec={spec} params={params} dispatch={updateSpec} version={version} rootPath={`${params.category}/${params.key}`} />
                        </Route>
                        <Route exact path={`/${params.env}/standards/${params.category}/${params.key}/${version}/overview`}>
                            <FrontPage spec={spec} dispatch={updateSpec} params={params} version={version} rootPath={`${params.category}/${params.key}`} />
                        </Route>
                        <Route exact path={`/${params.env}/standards/${params.category}/${params.key}`}>
                            <Redirect to={`/${params.env}/standards/${params.category}/${params.key}/latest/overview`} />
                        </Route>
                    </Switch>
                )}
            </Col>
            {hasRight && (
                <Col span={rightSpan}>
                    {rightTabItems.length === 1 ? (
                        rightTabItems[0].children
                    ) : (
                        <Tabs animated={false} items={rightTabItems} className="rightPanelTabs" />
                    )}
                </Col>
            )}
        </Row>
    );
}
